// @flow
import React, { Component } from 'react';
import ProductCardList from 'components/common/ProductCardList/ProductCardList';
import type { State } from 'store';
import { DisplayActions } from 'store/actionCreators';
import type { ProductItem } from 'store/modules/display';
import { connect } from 'react-redux';
import throttle from 'lodash/throttle';
import { getScrollBottom, preventStickBottom } from 'lib/common';
import { track, TRACKING_EVENTS } from 'lib/tracking';

type Props = {
  products: ?(ProductItem[]),
  prefetched: ?(ProductItem[]),
  prefetching: boolean,
  loading: boolean,
  width: number,
  hasEnded: boolean,
  selectedTag: ?{
    name: string,
    products_count: number,
  },
  shouldCancel: boolean,
  currentTag: ?string,
};

class TagProductCards extends Component<Props> {
  prevCursor: ?string = null;
  trackedListId: ?string = null;

  prefetch = async () => {
    const { products, prefetching, loading, selectedTag, hasEnded } = this.props;
    if (!selectedTag || !products || products.length === 0 || prefetching || loading) return;
    const lastId = products[products.length - 1].id;
    if (this.props.prefetched) {
      DisplayActions.revealPrefetched('tag');
      await Promise.resolve(); // next tick
    }
    if (lastId === this.prevCursor) return;
    this.prevCursor = lastId;
    if (hasEnded) return;
    try {
      await DisplayActions.prefetchTagProducts({
        cursor: lastId,
        tag: selectedTag.name,
      });
    } catch (e) {
      console.log(e);
    }
    preventStickBottom();
    this.onScroll();
  };

  onScroll = throttle(() => {
    const scrollBottom = getScrollBottom();
    if (scrollBottom > 1000) return;
    this.prefetch();
  }, 250);

  initialize = async () => {
    const { selectedTag, shouldCancel, currentTag, products } = this.props;
    if (selectedTag && currentTag === selectedTag.name && products && products.length > 0) {
      return;
    }
    if (!selectedTag) return;
    try {
      if (!shouldCancel) {
        await DisplayActions.getTagProducts({
          tag: selectedTag.name,
        });
      }
      await this.prefetch();
    } catch (e) {
      console.log(e);
    }
  };

  listenScroll = () => {
    window.addEventListener('scroll', this.onScroll);
  };

  unlistenScroll = () => {
    window.removeEventListener('scroll', this.onScroll);
  };

  componentDidMount() {
    this.initialize();
    this.listenScroll();
  }

  trackProductListViewed = () => {
    const { products, selectedTag } = this.props;
    const listId = selectedTag ? selectedTag.name : '';
    if (!products || products.length === 0) return;
    if (this.trackedListId === listId) return;

    const items = products.slice(0, 20).map(product => ({
      productId: product.id,
      productTitle: product.title,
    }));
    track(TRACKING_EVENTS.PRODUCT_LIST_VIEWED, {
      listId,
      listName: listId ? `tag:${listId}` : 'tag',
      items,
    });
    this.trackedListId = listId;
  };

  componentDidUpdate(prevProps) {
    this.trackProductListViewed();
    if (this.props.selectedTag !== prevProps.selectedTag) {
      this.initialize();
    }
  }

  componentWillUnmount() {
    this.unlistenScroll();
  }

  render() {
    const { selectedTag } = this.props;

    const placeholderCount = selectedTag ? selectedTag.products_count : 6;

    return (
      <ProductCardList
        products={this.props.products}
        loading={this.props.loading}
        prefetching={
          (this.props.prefetched && this.props.prefetched.length > 0) || this.props.prefetching
        }
        width={this.props.width}
        hasEnded={this.props.hasEnded}
        placeholderCount={placeholderCount > 12 ? 12 : placeholderCount}
        isTrending={false}
      />
    );
  }
}

const mapStateToProps = ({ display, pender, base, common }: State) => ({
  products: display.tag.products,
  prefetched: display.tag.prefetched,
  prefetching: pender.pending['display/PREFETCH_TAG_PRODUCTS'],
  loading: pender.pending['display/GET_TAG_PRODUCTS'],
  width: base.windowWidth,
  hasEnded: display.tag.end,
  selectedTag: common.productsTags.selected,
  shouldCancel: common.ssr && !common.router.altered,
  currentTag: display.tag.currentTag,
});

export default connect(mapStateToProps, () => ({}))(TagProductCards);



// WEBPACK FOOTER //
// ./src/containers/list/TagProductCards.js